import { useEffect, useState } from 'react';
import {
  useSetupOutputs,
  useToggleAudience,
  useTogglePersona,
  useToggleTheme,
  useToggleVoice,
  useUpdateBrandKit,
  type Audience,
  type BrandKit,
  type Persona,
  type SetupOutputs,
  type Theme,
  type Voice,
} from '../lib/setup';
import { StatusToggle } from './StatusToggle';
import { isDarkColor } from '../lib/textUtils';
import { useGenerateOne } from '../lib/setup';

interface Props {
  projectId: string;
  onClose: () => void;
  initialStep?: StepKey;
}

export type StepKey = 'audience' | 'voice' | 'persona' | 'theme' | 'brandkit';
const STEPS: { key: StepKey; label: string; room: string }[] = [
  { key: 'audience', label: 'Audience strategist', room: 'Audience' },
  { key: 'voice', label: 'Voice strategist', room: 'Voice' },
  { key: 'persona', label: 'Content strategists', room: 'Personas' },
  { key: 'theme', label: 'Campaign planner', room: 'Themes' },
  { key: 'brandkit', label: 'Visual designer', room: 'Visual style' },
];

/**
 * Five-step modal that walks the operator through curating the setup outputs.
 * Each step writes back to the existing isSelected / isActive flags, so the
 * content engine immediately picks up the reviewed brand memory on the next
 * post run.
 */
export function ChooseWizard({ projectId, onClose, initialStep = 'audience' }: Props) {
  const [stepIdx, setStepIdx] = useState(() => Math.max(0, STEPS.findIndex((s) => s.key === initialStep)));

  useEffect(() => {
    const idx = STEPS.findIndex((s) => s.key === initialStep);
    if (idx >= 0) setStepIdx(idx);
  }, [initialStep]);
  const setup = useSetupOutputs(projectId);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!setup.data) {
    return (
      <Backdrop onClose={onClose}>
        <Shell stepIdx={0} onClose={onClose}>
          <p className="font-mono text-xs uppercase tracking-wider text-muted text-center py-20">
            Loading prepared options…
          </p>
        </Shell>
      </Backdrop>
    );
  }

  const step = STEPS[stepIdx]!;
  return (
    <Backdrop onClose={onClose}>
      <Shell stepIdx={stepIdx} onClose={onClose}>
        {step.key === 'audience' && <AudienceStep data={setup.data} projectId={projectId} />}
        {step.key === 'voice' && <VoiceStep data={setup.data} projectId={projectId} />}
        {step.key === 'persona' && <PersonaStep data={setup.data} projectId={projectId} />}
        {step.key === 'theme' && <ThemeStep data={setup.data} projectId={projectId} />}
        {step.key === 'brandkit' && <BrandKitStep data={setup.data} projectId={projectId} onClose={onClose} />}
      </Shell>
    </Backdrop>
  );
}

// ───────── Shell + chrome ─────────

function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-background-dark/50 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl max-h-[90vh] flex shadow-2xl shadow-black/60">
        {children}
      </div>
    </div>
  );
}

function Shell({
  stepIdx,
  onClose,
  children,
}: {
  stepIdx: number;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col w-full bg-surface border border-border-color rounded-xl overflow-hidden">
      {/* Header — step indicator + close */}
      <header className="px-6 py-4 border-b border-border-color flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent-cyan">Strategy team 1:1</p>
          <h2 className="mt-1 font-display text-xl font-bold tracking-tight text-text-primary">{STEPS[stepIdx]!.label}</h2>
          <p className="mt-0.5 text-xs text-muted">{STEPS[stepIdx]!.room} briefing room</p>
        </div>
        <button
          onClick={onClose}
          className="material-symbols-outlined text-muted hover:text-text-primary transition-colors"
          aria-label="Close wizard"
        >
          close
        </button>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto scroll-thin p-4 md:p-6">{children}</div>

    </div>
  );
}

// ───────── Steps ─────────

function StepIntro({ title, helper }: { title: string; helper: string }) {
  return (
    <div className="mb-6">
      <h2 className="font-display text-2xl font-bold tracking-tight">{title}</h2>
      <p className="text-sm text-muted mt-1">{helper}</p>
    </div>
  );
}

function OptionCard({
  active,
  title,
  description,
  meta,
  tags,
  quoteItems,
  onToggle,
}: {
  active: boolean;
  title: string;
  description: string;
  meta?: string;
  tags?: string[];
  quoteItems?: string[];
  onToggle: () => void;
}) {
  return (
    <article
      className={`flex h-full w-full flex-col rounded-lg border p-5 transition-colors ${
        active
          ? 'bg-primary/10 border-primary'
          : 'bg-surface-2/40 border-border-color'
      }`}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
        <div className="min-w-0 pt-0.5">
          <p className="font-medium leading-tight text-text-primary">{title}</p>
          {meta && <p className="font-mono text-[10px] uppercase tracking-wider text-muted mt-0.5">{meta}</p>}
        </div>
        <StatusToggle active={active} label={title} onToggle={onToggle} />
      </div>
      <p className={`mt-4 text-sm text-muted leading-relaxed ${quoteItems?.length ? 'min-h-[5.25rem]' : ''}`}>
        {description}
      </p>
      {quoteItems && quoteItems.length > 0 && (
        <ul className="mt-4 space-y-1.5 text-xs italic leading-relaxed text-muted border-l-2 border-border-color pl-4">
          {quoteItems.map((item, i) => (
            <li key={i}>"{item}"</li>
          ))}
        </ul>
      )}
      {tags && tags.length > 0 && (
        <div className="mt-auto flex flex-wrap gap-1.5 pt-5">
          {tags.map((tag) => (
            <span key={tag} className="rounded-full border border-border-color/80 bg-background-dark/70 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-muted">
              {tag}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

function GenerateMoreButton({ projectId, kind, label }: { projectId: string; kind: 'audiences' | 'voices' | 'personas' | 'themes'; label: string }) {
  const generate = useGenerateOne(projectId, kind);
  const errorMessage = generate.error instanceof Error ? generate.error.message : null;
  return (
    <div className="mt-4 flex items-center gap-3 flex-wrap">
      <button
        type="button"
        onClick={() => generate.mutate()}
        disabled={generate.isPending}
        className="btn-bracket border border-accent-cyan/40 bg-accent-cyan/10 text-accent-cyan px-3 py-1.5 hover:bg-accent-cyan/20 disabled:opacity-40 transition-colors font-mono text-[11px] uppercase tracking-wider flex items-center gap-2"
      >
        <span className="material-symbols-outlined text-[14px]">add</span>
        {generate.isPending ? `Drafting one ${label}…` : `Generate one more ${label}`}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        One new card, distinct from existing names. Costs about $0.002.
      </p>
      {errorMessage && <span className="font-mono text-[10px] uppercase tracking-wider text-accent-red">{errorMessage}</span>}
    </div>
  );
}

function AudienceStep({ data, projectId }: { data: SetupOutputs; projectId: string }) {
  const toggle = useToggleAudience(projectId);
  return (
    <>
      <StepIntro
        title="Set active audiences"
        helper="Switch audience briefs on or off. Cards are for reading; only the ACTIVE control changes state."
      />
      {data.audiences.length === 0 && (
        <EmptyHint>No audiences yet. Prepare options first.</EmptyHint>
      )}
      <div className="grid sm:grid-cols-2 gap-3 items-stretch">
        {data.audiences.map((a: Audience) => (
          <OptionCard
            key={a.id}
            active={a.isSelected}
            title={a.name}
            description={a.summary}
            tags={a.traits}
            onToggle={() => toggle.mutate({ id: a.id, active: !a.isSelected })}
          />
        ))}
      </div>
      <GenerateMoreButton projectId={projectId} kind="audiences" label="audience" />
    </>
  );
}

function VoiceStep({ data, projectId }: { data: SetupOutputs; projectId: string }) {
  const toggle = useToggleVoice(projectId);
  return (
    <>
      <StepIntro
        title="Set active voices"
        helper="Switch writing briefs on or off. Cards are for reading; only the ACTIVE control changes state."
      />
      {data.voices.length === 0 && (
        <EmptyHint>No voices yet. Prepare options first.</EmptyHint>
      )}
      <div className="grid sm:grid-cols-2 gap-3 items-stretch">
        {data.voices.map((v: Voice) => (
          <OptionCard
            key={v.id}
            active={v.isSelected}
            title={v.name}
            description={v.description}
            quoteItems={v.examples}
            onToggle={() => toggle.mutate({ id: v.id, active: !v.isSelected })}
          />
        ))}
      </div>
      <GenerateMoreButton projectId={projectId} kind="voices" label="voice" />
    </>
  );
}

function PersonaStep({ data, projectId }: { data: SetupOutputs; projectId: string }) {
  const toggle = useTogglePersona(projectId);
  return (
    <>
      <StepIntro
        title="Set active personas"
        helper="Personas are recurring content angles. Cards are for reading; only the ACTIVE control changes state."
      />
      {data.personas.length === 0 && (
        <EmptyHint>No personas yet. Prepare options first.</EmptyHint>
      )}
      <div className="space-y-3">
        {data.personas.map((p: Persona) => (
          <OptionCard
            key={p.id}
            active={p.isActive}
            title={p.name}
            meta={`Carousel ${p.formatMixCarouselPct}% · Single ${p.formatMixSinglePct}%`}
            description={p.description}
            onToggle={() => toggle.mutate({ id: p.id, active: !p.isActive })}
          />
        ))}
      </div>
      <GenerateMoreButton projectId={projectId} kind="personas" label="persona" />
    </>
  );
}

function ThemeStep({ data, projectId }: { data: SetupOutputs; projectId: string }) {
  const toggle = useToggleTheme(projectId);
  return (
    <>
      <StepIntro
        title="Set active themes"
        helper="Themes are campaign territories. Cards are for reading; only the ACTIVE control changes state."
      />
      {data.themes.length === 0 && (
        <EmptyHint>No themes yet. Prepare options first.</EmptyHint>
      )}
      <div className="grid sm:grid-cols-2 gap-3 items-stretch">
        {data.themes.map((t: Theme) => (
          <OptionCard
            key={t.id}
            active={t.isActive}
            title={t.label}
            description={t.description}
            tags={t.exampleAngles}
            onToggle={() => toggle.mutate({ id: t.id, active: !t.isActive })}
          />
        ))}
      </div>
      <GenerateMoreButton projectId={projectId} kind="themes" label="theme" />
    </>
  );
}

function BrandKitStep({ data, projectId, onClose }: { data: SetupOutputs; projectId: string; onClose: () => void }) {
  const update = useUpdateBrandKit(projectId);
  const kit = data.brandKit;
  const [palette, setPalette] = useState(kit?.palette ?? defaultPalette);
  const [fonts, setFonts] = useState(kit?.fonts ?? { heading: 'Inter', body: 'Inter' });
  const [ttsVoice, setTtsVoice] = useState(kit?.ttsVoice ?? 'alloy');

  useEffect(() => {
    if (kit) {
      setPalette(kit.palette);
      setFonts(kit.fonts);
      setTtsVoice(kit.ttsVoice ?? 'alloy');
    }
  }, [kit?.id]);

  if (!kit) {
    return (
      <>
        <StepIntro
          title="Brand kit"
          helper="No brand kit yet. Brief your strategy team first."
        />
      </>
    );
  }

  const order: (keyof BrandKit['palette'])[] = ['primary', 'secondary', 'accent', 'background', 'text'];
  const dirty =
    order.some((k) => palette[k].toLowerCase() !== kit.palette[k].toLowerCase()) ||
    fonts.heading !== kit.fonts.heading ||
    fonts.body !== kit.fonts.body ||
    ttsVoice !== (kit.ttsVoice ?? 'alloy');

  return (
    <>
      <StepIntro
        title="Tune the brand kit"
        helper="Palette and fonts the Designer uses for every slide. Click a swatch to edit; save when ready."
      />
      <div className="grid grid-cols-5 gap-2 mb-4">
        {order.map((key) => (
          <div key={key} className="text-center">
            <label className="relative block h-20 w-full rounded-md border border-border-color cursor-pointer overflow-hidden">
              <div
                className="absolute inset-0"
                style={{ backgroundColor: palette[key] }}
                aria-hidden="true"
              />
              {/* Invisible input layered ON TOP of the swatch (not sr-only) so
                  the native colour picker anchors to this swatch instead of
                  appearing in the corner of the viewport. */}
              <input
                type="color"
                value={palette[key]}
                onChange={(e) =>
                  setPalette({ ...palette, [key]: e.target.value.toLowerCase() })
                }
                aria-label={`${key} colour`}
                className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
              />
            </label>
            <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-muted">{key}</p>
            <input
              type="text"
              value={palette[key]}
              onChange={(e) => setPalette({ ...palette, [key]: e.target.value.toLowerCase() })}
              spellCheck={false}
              className="mt-0.5 w-full bg-transparent text-center text-xs font-mono text-text-primary focus:outline-none focus:bg-surface-2 rounded px-1"
            />
          </div>
        ))}
      </div>
      {isDarkColor(palette.background) && (
        <div className="mb-4 rounded border border-accent-cyan/30 bg-accent-cyan/5 px-3 py-2 flex items-start gap-2">
          <span className="material-symbols-outlined text-accent-cyan text-[18px] shrink-0">dark_mode</span>
          <p className="text-xs text-text-primary leading-relaxed">
            <span className="font-mono uppercase tracking-wider text-accent-cyan">Dark mode</span> · your background is dark, so the renderer auto-flips body text to white, replaces the white body card with a subtler dark card, and uses high-contrast versions of the brand mark. Slide previews update live.
          </p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <FontPicker label="HEADING" value={fonts.heading} onChange={(v) => setFonts({ ...fonts, heading: v })} />
        <FontPicker label="BODY" value={fonts.body} onChange={(v) => setFonts({ ...fonts, body: v })} />
      </div>
      <div className="mt-4">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-2">Reel voice</p>
        <p className="text-xs text-muted mb-2 leading-relaxed">
          Picked when your content team narrates a reel. Each voice has a different character — match to how the brand sounds out loud.
        </p>
        <VoicePicker value={ttsVoice} onChange={setTtsVoice} />
      </div>
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          disabled={!dirty || update.isPending}
          onClick={async () => {
            try {
              await update.mutateAsync({ palette, fonts, ttsVoice });
              onClose();
            } catch {
              // Mutation error is surfaced by react-query; leave dialog open so
              // the operator can retry without losing their edits.
            }
          }}
          className="btn-bracket bg-primary text-white px-3 py-1.5 hover:bg-primary/90 disabled:opacity-40"
        >
          {update.isPending ? 'Saving…' : 'Save kit'}
        </button>
      </div>
    </>
  );
}

const SAFE_FONTS = [
  'Inter',
  'Source Sans 3',
  'Manrope',
  'Plus Jakarta Sans',
  'IBM Plex Sans',
  'Lora',
  'Source Serif 4',
  'Playfair Display',
  'Merriweather',
  'Space Grotesk',
  'JetBrains Mono',
] as const;

const REEL_VOICES = [
  { id: 'alloy', label: 'Alloy', hint: 'warm neutral, balanced' },
  { id: 'nova', label: 'Nova', hint: 'bright, optimistic, energetic' },
  { id: 'onyx', label: 'Onyx', hint: 'deep, masculine, weighty' },
  { id: 'sage', label: 'Sage', hint: 'measured, clear, professional' },
  { id: 'shimmer', label: 'Shimmer', hint: 'light, intimate, conversational' },
  { id: 'verse', label: 'Verse', hint: 'expressive, dynamic range' },
] as const;

function VoicePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {REEL_VOICES.map((v) => {
        const selected = v.id === value;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onChange(v.id)}
            className={`text-left rounded-lg border px-3 py-2 transition-colors ${
              selected
                ? 'border-accent-cyan bg-accent-cyan/10 text-text-primary'
                : 'border-border-color bg-surface-2 text-muted hover:text-text-primary hover:border-text-primary/40'
            }`}
          >
            <span className="block text-sm font-medium leading-tight">{v.label}</span>
            <span className="block font-mono text-[10px] uppercase tracking-wider mt-0.5 text-muted">{v.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

function FontPicker({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const isCustom = !SAFE_FONTS.includes(value as (typeof SAFE_FONTS)[number]);
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-1">{label}</p>
      <select
        value={isCustom ? '__custom' : value}
        onChange={(e) => onChange(e.target.value === '__custom' ? value : e.target.value)}
        className="bg-surface-2 border border-border-color rounded px-3 py-2 text-sm w-full text-text-primary focus:outline-none focus:border-accent-cyan"
        style={{ fontFamily: value }}
      >
        {SAFE_FONTS.map((f) => (
          <option key={f} value={f} style={{ fontFamily: f }}>
            {f}
          </option>
        ))}
        {isCustom && <option value="__custom">{value} (custom)</option>}
      </select>
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border-color p-10 text-center">
      <p className="font-mono text-xs uppercase tracking-wider text-muted">{children}</p>
    </div>
  );
}

const defaultPalette = {
  primary: '#111827',
  secondary: '#6b7280',
  accent: '#2563eb',
  background: '#ffffff',
  text: '#111827',
};
